import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useMemo } from 'react';

import { MembershipRole, TEAM_MEMBER_STATUS, type TeamTableRow } from '@ekhodealer/ekho-common';
import { useAuth } from '@ekhodealer/ekho-common/auth-client';
import {
	ActionMenu,
	ActionMenuItem,
	ActionMenuTrigger,
	Button,
	Empty,
	Group,
	MoreButton,
	Pagination,
	SkeletonRect,
	Tag,
	Text,
	toast,
} from '@ekhodealer/ekho-common/rev';

import { FloatingActionMenu } from '@common/components/FloatingActionMenu';
import NoLeads from '@common/components/icons/NoLeads';
import SquareUp24 from '@common/components/icons/SquareUp24';
import { SelectAllBanner } from '@common/components/SelectAllBanner';
import { Table, type TableColumn } from '@common/components/Table/Table';
import { PLACEHOLDER } from '@common/lib/strings';

import { useExportTeamMembers } from '../api/useExportTeamMembers';
import { useSearchTeamMembers } from '../api/useSearchTeamMembers';
import {
	currentPageAtom,
	itemsPerPageAtom,
	selectedTeamMembersAtom,
	teamParamsAtom,
	teamTabFilterAtom,
	updateItemsPerPageAtom,
	updateSelectedTeamMembersAtom,
} from '../atoms/teamAtoms';

import { RoleTag } from './RoleTag';

/**
 * Column IDs for the team table
 */
const TEAM_TABLE_COLUMN_ID = {
	NAME: 'name',
	EMAIL: 'email',
	ROLE: 'role',
	LAST_LOGIN: 'lastLogin',
	ACTIONS: 'actions',
} as const;

type TeamTableColumnId = (typeof TEAM_TABLE_COLUMN_ID)[keyof typeof TEAM_TABLE_COLUMN_ID];

/**
 * Action menu for individual team member rows
 */
function TeamMemberActionMenu({ item }: { item: TeamTableRow }): React.ReactNode {
	const exportMutation = useExportTeamMembers();

	const handleExport = async (): Promise<void> => {
		try {
			await exportMutation.mutateAsync({ memberIds: [item.id] });
			toast({ message: '1 team member exported', variant: 'success' });
		} catch {
			toast({ message: 'Failed to export team member. Please try again.', variant: 'error' });
		}
	};

	return (
		<ActionMenuTrigger>
			<MoreButton aria-label={`More actions for ${item.name}`} />
			<ActionMenu>
				<ActionMenuItem
					label='Export to CSV'
					leadingAccessory={<SquareUp24 />}
					onAction={handleExport}
				/>
			</ActionMenu>
		</ActionMenuTrigger>
	);
}

/**
 * Render a cell value based on column ID, with skeleton loading support
 */
function renderCell(
	columnId: TeamTableColumnId,
	item: TeamTableRow,
	isLoading: boolean,
	currentUserUid: string | undefined,
): React.ReactNode {
	if (isLoading) {
		return <SkeletonRect width={100} height={24} />;
	}

	switch (columnId) {
		case TEAM_TABLE_COLUMN_ID.NAME:
			return (
				<Group noWrap itemsSpacing='8' itemsAlignY='center' itemsAlignX='start'>
					<Text size='bodySmall' weight='semibold'>
						{item.name || PLACEHOLDER}
					</Text>
					{currentUserUid && item.id === currentUserUid && (
						<Tag size='small' accent='neutral' variant='pill' indicator={false}>
							You
						</Tag>
					)}
				</Group>
			);
		case TEAM_TABLE_COLUMN_ID.EMAIL:
			return <Text size='bodySmall'>{item.email || PLACEHOLDER}</Text>;
		case TEAM_TABLE_COLUMN_ID.ROLE:
			return item.role ? (
				<RoleTag role={item.role} />
			) : (
				<Text size='bodySmall'>{PLACEHOLDER}</Text>
			);
		case TEAM_TABLE_COLUMN_ID.LAST_LOGIN:
			return (
				<Text size='bodySmall'>
					{item.lastLogin ?? PLACEHOLDER}
				</Text>
			);
		case TEAM_TABLE_COLUMN_ID.ACTIONS:
			return <TeamMemberActionMenu item={item} />;
		default:
			return <Text size='bodySmall'>{PLACEHOLDER}</Text>;
	}
}

/**
 * TeamTable component
 * Displays a paginated, multi-select table of team members with row actions
 */
export function TeamTable(): React.ReactNode {
	const { user } = useAuth();
	const currentUserUid = user?.uid;

	// Tab filter
	const activeTab = useAtomValue(teamTabFilterAtom);
	const isPendingTab = activeTab === TEAM_MEMBER_STATUS.PENDING;

	// API params and data
	const params = useAtomValue(teamParamsAtom);
	const { data, isLoading } = useSearchTeamMembers(params);
	const items = useMemo(() => data?.data ?? [], [data?.data]);
	const queryCount = data?.count ?? 0;

	// Pagination state
	const [currentPage, setCurrentPage] = useAtom(currentPageAtom);
	const itemsPerPage = useAtomValue(itemsPerPageAtom);
	const updateItemsPerPage = useSetAtom(updateItemsPerPageAtom);

	// Selection state
	const selectedTeamMembers = useAtomValue(selectedTeamMembersAtom);
	const updateSelectedTeamMembers = useSetAtom(updateSelectedTeamMembersAtom);

	// Export
	const exportMutation = useExportTeamMembers();

	const isAllPageSelected = selectedTeamMembers === 'all';
	const selectedCount = isAllPageSelected
		? items.length
		: (selectedTeamMembers as Set<string>).size;

	const hasEmptyResults = !isLoading && items.length === 0;

	const handlePageChange = (page: number): void => {
		setCurrentPage(page);
	};

	const handleItemsPerPageChange = (newItemsPerPage: number): void => {
		updateItemsPerPage(newItemsPerPage);
	};

	// Skeleton placeholder items while loading
	const getDisplayItems = (): TeamTableRow[] => {
		if (isLoading) {
			return Array.from({ length: itemsPerPage }, (_, index) => ({
				id: `skeleton-${index}`,
				name: '',
				email: '',
				role: MembershipRole.MEMBER,
				lastLogin: null,
				status: '' as TeamTableRow['status'],
			}));
		}
		return items;
	};

	// Empty state
	if (hasEmptyResults) {
		return (
			<Empty
				icon={<NoLeads />}
				title='No team members found'
				description='Try adjusting your filters or invite new members to get started.'
			/>
		);
	}

	const allColumns: TableColumn<TeamTableRow>[] = [
		{
			id: TEAM_TABLE_COLUMN_ID.NAME,
			title: 'Name',
			sortable: false,
			renderCell: (item) =>
				renderCell(TEAM_TABLE_COLUMN_ID.NAME, item, isLoading, currentUserUid),
		},
		{
			id: TEAM_TABLE_COLUMN_ID.EMAIL,
			title: 'Email',
			sortable: false,
			renderCell: (item) =>
				renderCell(TEAM_TABLE_COLUMN_ID.EMAIL, item, isLoading, currentUserUid),
		},
		{
			id: TEAM_TABLE_COLUMN_ID.ROLE,
			title: 'Role',
			sortable: false,
			renderCell: (item) =>
				renderCell(TEAM_TABLE_COLUMN_ID.ROLE, item, isLoading, currentUserUid),
		},
		{
			id: TEAM_TABLE_COLUMN_ID.LAST_LOGIN,
			title: 'Last active',
			sortable: false,
			renderCell: (item) =>
				renderCell(TEAM_TABLE_COLUMN_ID.LAST_LOGIN, item, isLoading, currentUserUid),
		},
		{
			id: TEAM_TABLE_COLUMN_ID.ACTIONS,
			title: '',
			sortable: false,
			renderCell: (item) =>
				renderCell(TEAM_TABLE_COLUMN_ID.ACTIONS, item, isLoading, currentUserUid),
			renderHeader: () => null,
		},
	];

	const columns = isPendingTab
		? allColumns.filter((col) => col.id !== TEAM_TABLE_COLUMN_ID.NAME)
		: allColumns;

	return (
		<>
			<Table<TeamTableRow>
				columns={columns}
				items={getDisplayItems()}
				getItemId={(item) => item.id}
				selectionMode='multiple'
				selectedItems={selectedTeamMembers}
				onSelectionChange={updateSelectedTeamMembers}
				aria-label={isLoading ? 'Loading team members table' : 'Team members table'}
				stickyRightCount={1}
				belowTableContent={
					!hasEmptyResults &&
					!isLoading && (
						<Pagination
							variant='contained'
							currentPage={currentPage}
							itemsPerPage={itemsPerPage}
							totalItems={queryCount}
							itemsPerPageOptions={[10, 25, 50, 100]}
							onNavigateTo={handlePageChange}
							onItemsPerPageChange={handleItemsPerPageChange}
							aria-label='Team members table pagination'
						/>
					)
				}
			/>
			<FloatingActionMenu
				isOpen={selectedCount > 0}
				onClose={() => {
					updateSelectedTeamMembers(new Set());
				}}
				closeButtonTooltip='Deselect all'
				leftContent={
					<SelectAllBanner
						selectedCount={
							isAllPageSelected
								? items.length
								: (selectedTeamMembers as Set<string>).size
						}
						totalCount={queryCount}
						isAllPageSelected={isAllPageSelected}
						isAllMatchingSelected={false}
						onSelectAllMatching={() => {
							/* Not supported for team table yet */
						}}
						onClearSelection={() => updateSelectedTeamMembers(new Set())}
						resourceName='team members'
					/>
				}
				rightContent={
					<Group noWrap itemsSpacing='8'>
						<Button
							size='medium'
							variant='secondary'
							rounding='10'
							isDisabled={exportMutation.isPending}
							onPress={async () => {
								const ids =
									selectedTeamMembers === 'all'
										? items.map((item) => item.id)
										: Array.from(selectedTeamMembers as Set<string>);
								await exportMutation.mutateAsync({ memberIds: ids });
								toast({
									message: 'Team members exported successfully',
									variant: 'success',
								});
								updateSelectedTeamMembers(new Set());
							}}>
							{exportMutation.isPending ? 'Exporting...' : 'Export'}
						</Button>
					</Group>
				}
			/>
		</>
	);
}
